import { graphql } from 'react-apollo';

import type { Maybe } from '__generated__/graphql-types';
import { compose } from 'recompose';

import FullSkillCollectionListBySkillQuery from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/queries/FullSkillCollectionListBySkill.graphql';
import PreviewSkillCollectionListBySkillQuery from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/queries/PreviewSkillCollectionListBySkill.graphql';
import SkillCollectionListBySkillSetQuery from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/queries/SkillCollectionListBySkillSet.graphql';
import type {
  FullSkillCollectionListBySkillQuery as FullSkillCollectionListBySkillQueryData,
  FullSkillCollectionListBySkillQueryVariables,
} from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/queries/__generated__/FullSkillCollectionListBySkill';
import type {
  PreviewSkillCollectionListBySkillQuery as PreviewSkillCollectionListBySkillQueryData,
  PreviewSkillCollectionListBySkillQueryVariables,
} from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/queries/__generated__/PreviewSkillCollectionListBySkill';
import type {
  SkillCollectionListBySkillSetQueryQuery as SkillCollectionListBySkillSetQueryQueryData,
  SkillCollectionListBySkillSetQueryQueryVariables,
} from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/queries/__generated__/SkillCollectionListBySkillSet';

import type { FullCollectionListItemFragmentWithLimitsFragment } from './queries/fragments/__generated__/FullCollectionListItemFragmentWithLimits';
import type { PreviewCollectionListItemFragmentWithLimitsFragment } from './queries/fragments/__generated__/PreviewCollectionListItemFragmentWithLimits';

export type PreviewCollectionResult = PreviewCollectionListItemFragmentWithLimitsFragment;

export type FullCollectionResult = FullCollectionListItemFragmentWithLimitsFragment;

export type CollectionResult = FullCollectionResult | PreviewCollectionResult;

/**
 * These components are designed to work with the SkillPageContent component.
 * e.g. They rely on it's error boundary being present for error handling and the fact that we
 * have different versions for optimising page renders. Please take this into consideration if
 * you want to use for another component.
 */
export type PropsFromCaller = {
  userId: number;
  programId: string;
  targetSkillProfileId?: string;
  skillId: string;
};

export type PropsFromPreviewSkillCollectionGraphql = {
  previewCollection?: Maybe<CollectionResult>;
  isPreviewCollectionLoading: boolean;
};

export type PropsFromFullSkillCollectionGraphql = {
  fullCollection: CollectionResult | null | undefined;
  isFullCollectionLoading: boolean;
};

// This HOC fetches the recommendations by skill and skillset
// It is skipped if no skillset is available
export const withSkillCollectionBySkillSet = graphql<
  PropsFromCaller,
  SkillCollectionListBySkillSetQueryQueryData,
  SkillCollectionListBySkillSetQueryQueryVariables,
  PropsFromFullSkillCollectionGraphql
>(SkillCollectionListBySkillSetQuery, {
  // We add default for targetSkillProfileId to avoid the ts error that it is undefined in the query vs ignoring TS for the whole line
  // This is okay because we skip the query if it is not available
  options: ({ programId, targetSkillProfileId = '', skillId }) => ({
    notifyOnNetworkStatusChange: true,
    variables: {
      programId,
      targetSkillProfileId,
      skillId,
      numEntriesPerCollection: 300,
    },
    context: { clientName: 'gatewayGql' },
  }),
  skip: ({ targetSkillProfileId }) => !targetSkillProfileId,
  props: ({ data }): PropsFromFullSkillCollectionGraphql => {
    const fullCollection =
      data?.NaptimeQueries?.ProgramCurriculumCollectionsV1Resource?.skillCollectionByTargetProfileIdAndSkillId
        ?.elements?.[0];

    if (data?.error) {
      // Caught by error boundary in SkillPageContent
      throw data?.error;
    }
    return {
      fullCollection,
      isFullCollectionLoading: data?.loading ?? true,
    };
  },
});

// This HOC fetches the recommendations by skill only
// It is skipped if a skillset is available
// 'Preview' fetches only the first page of information and only the data required for the product cards to optimise LCP
export const withSkillCollectionPreviewBySkill = graphql<
  PropsFromCaller,
  PreviewSkillCollectionListBySkillQueryData,
  PreviewSkillCollectionListBySkillQueryVariables,
  PropsFromPreviewSkillCollectionGraphql
>(PreviewSkillCollectionListBySkillQuery, {
  options: ({ programId, skillId }) => ({
    ssrBlocking: true,
    notifyOnNetworkStatusChange: true,
    variables: {
      programId,
      skillId,
      numEntriesPerCollection: 24,
    },
    context: { clientName: 'gatewayGql' },
  }),
  skip: ({ targetSkillProfileId }) => !!targetSkillProfileId,
  props: ({ data }): PropsFromPreviewSkillCollectionGraphql => {
    const previewCollection =
      data?.NaptimeQueries?.ProgramCurriculumCollectionsV1Resource?.skillCollectionBySkillId?.elements?.[0];
    if (data?.error) {
      // Caught by error boundary in SkillPageContent
      throw data?.error;
    }

    return {
      previewCollection,
      isPreviewCollectionLoading: data?.loading ?? true,
    };
  },
});

// 'Full' fetches all the information required for the filters
export const withFullSkillCollectionBySkill = graphql<
  PropsFromCaller,
  FullSkillCollectionListBySkillQueryData,
  FullSkillCollectionListBySkillQueryVariables,
  PropsFromFullSkillCollectionGraphql
>(FullSkillCollectionListBySkillQuery, {
  options: ({ programId, skillId }) => ({
    ssr: false, // Should always run after initial render
    notifyOnNetworkStatusChange: true,
    variables: {
      programId,
      skillId,
      numEntriesPerCollection: 300,
    },
    context: { clientName: 'gatewayGql' },
  }),
  skip: ({ targetSkillProfileId }) => !!targetSkillProfileId,
  props: ({ data }): PropsFromFullSkillCollectionGraphql => {
    const fullCollection =
      data?.NaptimeQueries?.ProgramCurriculumCollectionsV1Resource?.skillCollectionBySkillId?.elements?.[0];
    if (data?.error) {
      // Caught by error boundary in SkillPageContent
      throw data?.error;
    }
    return {
      fullCollection,
      isFullCollectionLoading: data?.loading ?? true,
    };
  },
});

export const withSkillCollectionData = compose<
  PropsFromPreviewSkillCollectionGraphql & PropsFromFullSkillCollectionGraphql,
  PropsFromCaller
>(withSkillCollectionBySkillSet, withSkillCollectionPreviewBySkill);
export default withSkillCollectionData;
